import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonFab,
  IonFabButton,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { add } from 'ionicons/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMasters } from '../../lib/data/queries.js';
import { EquipmentEditorModal } from '../../components/editors/EquipmentEditorModal.js';
import { FeatureIntroBlock, isIntroDismissed } from '../../components/FeatureCard.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';

interface CleaningEquipment {
  id: string;
  name: string;
  kind: string;
}

// T-149: schedule collegate, per indicare quali attrezzature sono nel registro.
interface CleaningScheduleLink {
  id: string;
  equipment_id: string | null;
  equipment_ids?: string[] | null;
}

export function CleaningEquipmentsPage(): JSX.Element {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { data: equipments = [] } = useMasters<CleaningEquipment>('cleaning_equipments');
  const { data: schedules = [] } = useMasters<CleaningScheduleLink>('cleaning_schedules');
  const [toast, setToast] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [introVersion, setIntroVersion] = useState(0);
  const featureKey = 'cleaning_equipments';
  const userId = profile?.id ?? null;
  const businessId = profile?.active_business_id ?? null;
  const showIntro = equipments.length === 0
    && !isIntroDismissed(featureKey, userId, businessId);
  void introVersion;

  // T-149: insieme degli equipment_id che hanno almeno una schedule di pulizia
  // collegata (single equipment_id o nell'array equipment_ids). Un'attrezzatura
  // "collegata" compare nelle matrici del registro mensile esportato.
  const linkedEquipmentIds = new Set<string>();
  for (const s of schedules) {
    if (s.equipment_id) linkedEquipmentIds.add(s.equipment_id);
    for (const eid of s.equipment_ids ?? []) linkedEquipmentIds.add(eid);
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/haccp/cleaning" />
          </IonButtons>
          <IonTitle>Attrezzature pulizia</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        <div style={{ padding: '12px 16px 96px' }}>
          {showIntro ? (
            <FeatureIntroBlock
              featureKey={featureKey}
              icon="🔧"
              title="Attrezzature pulizia"
              description={
                <>
                  Registra le <b>attrezzature</b> usate per la sanificazione
                  (panni, spugne, mop, secchi, lavastoviglie, sterilizzatori
                  UV, ecc.). Ogni attrezzatura ha un nome e un tipo: questo
                  permette di collegarla ai piani di pulizia e dimostrare
                  che gli interventi sono stati fatti con strumenti idonei.
                  <br /><br />
                  Servono soprattutto in caso di ispezione ASL e per il
                  Manuale HACCP — le attrezzature elencate qui finiscono
                  nella sezione "Mezzi e materiali" del documento.
                </>
              }
              whatsappQuestion="Ciao, ho dubbi su come gestire le attrezzature di pulizia in TrackEAT, mi puoi aiutare?"
              startLabel="Aggiungi la prima attrezzatura"
              onStart={() => { setIntroVersion((v) => v + 1); setEditorOpen(true); }}
              userId={userId}
              businessId={businessId}
              flush
            />
          ) : equipments.length === 0 ? (
            <div style={{ background: '#fff', padding: 16, borderRadius: 14, color: '#64748b', fontStyle: 'italic', fontSize: 13 }}>
              {t('common.empty')}
            </div>
          ) : (
            <>
              {/* T-149: spiega il collegamento attrezzatura → registro mensile. */}
              <div style={{
                background: '#eef2ff',
                border: '1px solid #c7d2fe',
                borderRadius: 12,
                padding: '10px 12px',
                margin: '0 0 12px',
                fontSize: 12.5,
                color: '#3730a3',
                lineHeight: 1.4,
              }}>
                Ogni attrezzatura con una pulizia collegata
                {' '}<span style={{ fontWeight: 700 }}>compare nel registro mensile</span>{' '}
                e può essere spuntata giorno per giorno. Quando aggiungi
                un'attrezzatura, l'app crea in automatico l'attività di
                sanificazione.
              </div>
              {equipments.map((e) => {
                const linked = linkedEquipmentIds.has(e.id);
                return (
                  <div
                    key={e.id}
                    style={{
                      background: '#fff',
                      borderRadius: 14,
                      borderLeft: `4px solid ${linked ? '#7c3aed' : '#cbd5e1'}`,
                      boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                      margin: '10px 0',
                      padding: '12px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                    }}
                  >
                    <div style={{
                      width: 38, height: 38, borderRadius: 10,
                      background: '#ede9fe', color: '#7c3aed',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 20, flexShrink: 0,
                    }}>
                      🔧
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 15, color: '#0f172a' }}>{e.name}</div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{e.kind}</div>
                    </div>
                    <span style={{
                      flexShrink: 0,
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '3px 9px',
                      borderRadius: 999,
                      background: linked ? '#dcfce7' : '#f1f5f9',
                      color: linked ? '#15803d' : '#64748b',
                    }}>
                      {linked ? 'Nel registro' : 'Non nel registro'}
                    </span>
                  </div>
                );
              })}
            </>
          )}
        </div>

        <IonFab slot="fixed" vertical="bottom" horizontal="end">
          <IonFabButton aria-label="Nuova attrezzatura" onClick={() => setEditorOpen(true)}>
            <IonIcon icon={add} />
          </IonFabButton>
        </IonFab>

        <EquipmentEditorModal
          isOpen={editorOpen}
          autoCreateSchedule
          onDismiss={() => setEditorOpen(false)}
          onSaved={() => setToast('Attrezzatura creata. Aggiunta al registro pulizie.')}
        />

        <IonToast
          isOpen={!!toast}
          message={toast ?? ''}
          duration={1500}
          onDidDismiss={() => setToast(null)}
        />
      </IonContent>
    </IonPage>
  );
}
